/**
 * Weekly session batch generator — turns one curriculum WeeklyTopic into a
 * set of reviewable session drafts. First real consumer of
 * `buildSessionPlanPrompt` (§18.1). Pure mapping + date logic is exported
 * for unit testing; the AI call + generation-context read are injected via
 * `@4cm/core/ai` + `@4cm/brain/generationContext` so tests mock them.
 *
 * The drafts are NOT saved here — the Season page renders each as a
 * DraftCard and the coach approves them one by one via `commitSessionPlan`
 * (mirrors the SessionNew approve loop). Nothing hits the DB unapproved.
 */
import { callAIJson } from '@4cm/core/ai';
import {
  buildSessionPlanPrompt,
  type SessionPlanInput,
  type SessionPlanResult,
} from '@4cm/core/sessionPlannerPrompt';
import type { CurriculumPlan, WeeklyTopic } from '@4cm/core/curriculumPrompt';
import { buildGenerationContext } from '@4cm/brain/generationContext';
import type { SessionPlanDraft } from '@4cm/brain/draftCommit';
import type { TeamFull } from './queries/useTeamFull';

type WeeklySurface = 'calendar-week' | 'calendar-all-weeks';

/** Curriculum corner tag → seed corner id (packages/shared/corners.ts). */
const CORNER_TAG_TO_ID: Record<string, string> = {
  technical_tactical: 'corner-technical',
  physical: 'corner-physical',
  psychological: 'corner-psych',
  social: 'corner-social',
};

/** Weekday offsets from the week's Monday for N sessions — a sensible
 *  default spread (Tue/Thu for 2, Tue/Thu/Sat for 3, …). Used when the
 *  team's actual training weekdays aren't resolved here. */
const DAY_OFFSETS: Record<number, number[]> = {
  1: [2],
  2: [1, 3],
  3: [1, 3, 5],
  4: [0, 2, 4, 6],
  5: [0, 1, 3, 4, 6],
  6: [0, 1, 2, 4, 5, 6],
  7: [0, 1, 2, 3, 4, 5, 6],
};

function parseMonday(dateStart: string): Date {
  // Parse as local midnight to avoid the UTC day-shift `new Date('YYYY-MM-DD')`
  // causes. Fall back to today if the curriculum gave a bad date.
  const d = new Date(`${dateStart}T00:00:00`);
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function toLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Session dates for a week — clamps `count` to [1,7] and spreads sessions
 * across weekdays from the week's Monday. Returns ISO timestamps at 18:00
 * local (a sane default training time; avoids midnight TZ day-shift).
 */
export function weekSessionDates(dateStart: string, count: number): string[] {
  const n = Math.max(1, Math.min(Math.floor(count) || 1, 7));
  const offsets = DAY_OFFSETS[n] ?? DAY_OFFSETS[3];
  const base = parseMonday(dateStart);
  return offsets.map((off) => {
    const d = new Date(base);
    d.setDate(d.getDate() + off);
    return `${toLocalDate(d)}T18:00:00`;
  });
}

/** Map a generated SessionPlanResult into the SessionPlanDraft shape
 *  `commitSessionPlan` consumes, anchoring it to a date + curriculum week. */
export function mapSessionResultToDraft(
  result: SessionPlanResult,
  opts: { teamId: string; date: string; curriculumWeek: number },
): SessionPlanDraft {
  const drills = (result.drills ?? []).map((d) => ({
    name: d.name,
    description: [d.setup_description, d.instructions].filter(Boolean).join('\n\n'),
    drill_type: d.type,
    primary_phase: d.primary_phase ?? null,
    sub_themes: d.sub_themes ?? [],
    duration_mins: d.duration_mins,
    corner_ids: (d.corner_tags ?? [])
      .map((t) => CORNER_TAG_TO_ID[t])
      .filter((id): id is string => Boolean(id)),
    sub_corner_ids: d.sub_corner_tags ?? [],
    min_players: d.players_required,
    max_players: d.players_required,
    space_required: d.space,
    equipment: d.equipment ?? [],
    coaching_points: d.coaching_points ?? [],
    progressions: d.progressions ?? [],
    decision_under_execution: d.decision_under_execution,
  }));
  const totalDuration = drills.reduce((sum, d) => sum + (d.duration_mins || 0), 0);
  return {
    team_id: opts.teamId,
    date: opts.date,
    title: result.session_title,
    duration_mins: result.estimated_duration_mins || totalDuration || 60,
    objectives: result.session_objective ? [result.session_objective] : [],
    psych_social_intent: result.psych_social_intent ?? '',
    phase_coverage: result.phase_coverage,
    corner_coverage: result.corner_coverage,
    curriculum_week: opts.curriculumWeek,
    drills,
  };
}

/** Local "what + why these sessions" summary — derived from the week topic
 *  + generated session titles. No extra AI call. */
export function buildWeekSummary(week: WeeklyTopic, drafts: SessionPlanDraft[]): string {
  if (drafts.length === 0) {
    return `Week ${week.week_number}: ${week.topic}. Couldn't draft sessions just now — try again.`;
  }
  const titles = drafts.map((d) => d.title).filter(Boolean);
  const what = `Week ${week.week_number} — ${week.topic}: ${drafts.length} session${drafts.length === 1 ? '' : 's'}${
    titles.length ? ` (${titles.join('; ')})` : ''
  }.`;
  const why = week.principle_connection
    ? ` Why: builds toward "${week.principle_connection}".`
    : ` Why: keeps the squad on this week's focus before moving on.`;
  return what + why;
}

export interface GenerateWeekArgs {
  team: TeamFull;
  coachId: string;
  week: WeeklyTopic;
  /** Phase label this week belongs to (e.g. "Mid-season") — seeds seasonPhase. */
  phaseLabel?: string;
  surface?: WeeklySurface;
  signal?: AbortSignal;
}

export interface WeekPlanResult {
  weekNumber: number;
  weekSummary: string;
  drafts: SessionPlanDraft[];
  /** Count of sessions the AI failed to generate (null/parse fail). */
  failures: number;
}

/**
 * Generate the sessions for one curriculum week. Builds the <priors> block
 * once, then generates `session_count` sessions (clamped to [1,7]) seeded
 * by the week's topic / sub-corners / principle / intensity.
 */
export async function generateWeekSessions(args: GenerateWeekArgs): Promise<WeekPlanResult> {
  const { team, coachId, week, phaseLabel, surface = 'calendar-week', signal } = args;
  const dates = weekSessionDates(week.date_start, week.session_count);

  const ctx = await buildGenerationContext({
    coachId,
    teamId: team.id,
    entity: 'session',
    biasTags: [`corner:${week.primary_corner}`, `week:${week.week_number}`],
  });

  const drafts: SessionPlanDraft[] = [];
  let failures = 0;

  for (let i = 0; i < dates.length; i += 1) {
    if (signal?.aborted) break;
    const input: SessionPlanInput = {
      ageGroup: team.age_group ?? 'U12',
      durationMins: team.session_duration_mins ?? 60,
      playerCount: team.player_count ?? 12,
      primaryFocus: week.topic,
      secondaryFocus: week.sub_corners?.length ? week.sub_corners.join(', ') : undefined,
      equipment: [],
      seasonPhase: phaseLabel ?? 'in-season',
      constraints:
        [week.principle_connection, week.intensity ? `intensity: ${week.intensity}` : '']
          .filter(Boolean)
          .join('; ') || undefined,
      priorsBlock: ctx.block || undefined,
    };
    const result = await callAIJson<SessionPlanResult>({
      system: buildSessionPlanPrompt(input),
      userContent:
        dates.length > 1
          ? `Generate session ${i + 1} of ${dates.length} for this week. Vary it from the week's other sessions while staying on the focus. Return only the JSON.`
          : 'Generate the session plan. Return only the JSON.',
      // 4000 (not the §18 default 2000): a complete session — warm-up →
      // technical → small-sided game → cool-down with full coaching points —
      // truncates at 2000, stopping ~halfway with no game phase (Brain eval).
      surface,
      maxTokens: 4000,
      signal,
    });
    if (!result || !Array.isArray(result.drills)) {
      failures += 1;
      continue;
    }
    drafts.push(
      mapSessionResultToDraft(result, {
        teamId: team.id,
        date: dates[i],
        curriculumWeek: week.week_number,
      }),
    );
  }

  return { weekNumber: week.week_number, weekSummary: buildWeekSummary(week, drafts), drafts, failures };
}

export interface AllWeeksProgress {
  week: number;
  total: number;
}

/**
 * Generate sessions for every week of a curriculum, sequentially (protects
 * the token budget + keeps ordering). Uses the Haiku-pinned
 * 'calendar-all-weeks' surface. Honours an AbortController.
 */
export async function generateAllWeeks(args: {
  team: TeamFull;
  coachId: string;
  plan: CurriculumPlan;
  signal?: AbortSignal;
  onProgress?: (p: AllWeeksProgress) => void;
}): Promise<WeekPlanResult[]> {
  const { team, coachId, plan, signal, onProgress } = args;
  const queue: Array<{ week: WeeklyTopic; phaseLabel: string }> = [];
  for (const phase of plan.phase_plans ?? []) {
    for (const w of phase.weekly_topics ?? []) queue.push({ week: w, phaseLabel: phase.phase });
  }
  const results: WeekPlanResult[] = [];
  for (let i = 0; i < queue.length; i += 1) {
    if (signal?.aborted) break;
    onProgress?.({ week: i + 1, total: queue.length });
    results.push(
      await generateWeekSessions({
        team,
        coachId,
        week: queue[i].week,
        phaseLabel: queue[i].phaseLabel,
        surface: 'calendar-all-weeks',
        signal,
      }),
    );
  }
  return results;
}
